import { create } from 'zustand';
import { defaultWMS2040_R01HeaderDto, WMS2040_R01HeaderDto, WMS2040_R01DetailDto } from 'src/containers/mes/WMS/WMS2040_R01/Dto_WMS2040_R01';
import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';


export interface WMS2040_R01Filter {
	compCd: string;          // 사업장코드
	txDt: DateRange | undefined; // 수불연월 (날짜 범위)
	logicCenter: string;     // 물류센터코드
	warehouseCd: string;     // 창고코드
	brandCd: string;         // 브랜드코드
	partnerCd: string;       // 거래처코드
	partnerNm: string;       // 거래처명
	itemCd: string;          // 품목코드
	itemNm: string;          // 품목명
	itemGroup: string;       // 품목그룹
	itemStatus: string;      // 품목등급
	startDate: string;       // 시작일자
	endDate: string;         // 종료일자
	searchText: string;      // 검색텍스트
	includeZeroStock: boolean;  // 재고0 포함 여부
}

// 품목 정보 타입
export interface SelectedItemInfo {
	itemCd: string;
	itemNm: string;
}

// 선택된 행의 복합 키 정보 타입
export interface SelectedRowInfo {
	itemCd: string;
	warehouseCd: string;
	logicCenter: string;
	itemStatus: string;
	valueIndex: number;
}

export interface Store_WMS2040_R01 {

	// 검색 조건 (백엔드로 전송)
	WMS2040_R01Filter: WMS2040_R01Filter;
	WMS2040_R01PastFilter: WMS2040_R01Filter;
	setWMS2040_R01Filter: (filter: Partial<WMS2040_R01Filter>) => void;
	setWMS2040_R01PastFilter: (filter: Partial<WMS2040_R01Filter>) => void;

	// 재고 현황 목록 (좌측)
	WMS2040_R01List: WMS2040_R01HeaderDto[];
	setWMS2040_R01List: (list: WMS2040_R01HeaderDto[]) => void;
	
	// 재고 원장 상세 목록 (우측)
	WMS2040_R01DetailList: WMS2040_R01DetailDto[];
	setWMS2040_R01DetailList: (list: WMS2040_R01DetailDto[]) => void;

	// 상세(디테일) 행 선택/삭제 관리
	deletedWMS2040_R01DetailList: string[];
	setDeletedWMS2040_R01DetailList: (idList: string[]) => void;
	addDeletedWMS2040_R01DetailList: (idList: string[]) => void;
	removeDeletedWMS2040_R01DetailList: (idList?: string[]) => void;

	selectedWMS2040_R01DetailList: string | undefined;
	setSelectedWMS2040_R01DetailList: (id: string | undefined) => void;
	initSelectedWMS2040_R01DetailList: () => void;

	originalWMS2040_R01DetailList: WMS2040_R01DetailDto[];
	setOriginalWMS2040_R01DetailList: (data: WMS2040_R01DetailDto[]) => void;
	
	// 선택된 품목 (하위 호환성을 위해 유지)
	selectedWMS2040_R01: string | undefined;
	setSelectedWMS2040_R01: (itemCd: string | undefined) => void;
	initSelectedWMS2040_R01: () => void;
	
	// 선택된 행의 복합 키 정보 (신규 추가)
	selectedRowInfo: SelectedRowInfo | undefined;
	setSelectedRowInfo: (rowInfo: SelectedRowInfo | undefined) => void;

	// 검색 조건에서 선택된 품목 정보
	selectedItemInfo: SelectedItemInfo | undefined;
	setSelectedItemInfo: (itemInfo: SelectedItemInfo | undefined) => void;

	// 컬럼 상태 관리
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	allCount: number;					setAllCount: (allCount: number) => void;
	detailFetchTrigger: boolean;
	setDetailFetchTrigger: (trigger: boolean) => void;

	// 초기화
	initData: () => void;
}

export const defaultWMS2040_R01Filter : WMS2040_R01Filter = {
	compCd: '',
	txDt:  {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1), // 이번 달 1일
		to: new Date()  // 오늘
	},
	logicCenter: '',
	warehouseCd: '',
	brandCd: '',
	partnerCd: '',
	partnerNm: '',
	itemCd: '',
	itemNm: '',
	itemGroup: '',
	itemStatus: '',
	startDate: '',
	endDate: '',
	searchText: '',
	includeZeroStock: false,  // 기본: 재고0 제외
};

export const useWMS2040_R01Store = create<Store_WMS2040_R01>((set, get) => ({

	// 검색 조건 (백엔드로 전송)
	WMS2040_R01Filter: defaultWMS2040_R01Filter,
	WMS2040_R01PastFilter: defaultWMS2040_R01Filter,
	setWMS2040_R01Filter: (filter) => set((state) => ({ WMS2040_R01Filter: { ...state.WMS2040_R01Filter, ...filter } })),
	setWMS2040_R01PastFilter: (filter) => set((state) => ({ WMS2040_R01PastFilter: { ...state.WMS2040_R01PastFilter, ...filter } })),

	// 재고 현황 목록
	WMS2040_R01List: [],
	setWMS2040_R01List: (list: WMS2040_R01HeaderDto[]) => set((state) => {
		let newList: WMS2040_R01HeaderDto[] = [];
		let valueAndOrderIndex = 1;
		list.forEach((item) => {
			newList.push({ 
				...defaultWMS2040_R01HeaderDto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			WMS2040_R01List: newList
		}
	}),

	// 재고 원장 상세 목록
	WMS2040_R01DetailList: [],
	setWMS2040_R01DetailList: (list: WMS2040_R01DetailDto[]) => set({ WMS2040_R01DetailList: list }),

	// 상세 삭제/선택/원본 관리
	deletedWMS2040_R01DetailList: [],
	setDeletedWMS2040_R01DetailList: (idList: string[]) => set({ deletedWMS2040_R01DetailList: idList }),
	addDeletedWMS2040_R01DetailList: (idList: string[]) => set((state) => ({
		deletedWMS2040_R01DetailList: [...state.deletedWMS2040_R01DetailList, ...idList]
	})),
	removeDeletedWMS2040_R01DetailList: (idList?: string[]) => set((state) => ({
		deletedWMS2040_R01DetailList: idList ? state.deletedWMS2040_R01DetailList.filter(v => !idList.includes(v)) : []
	})),

	selectedWMS2040_R01DetailList: undefined,
	setSelectedWMS2040_R01DetailList: (id: string | undefined) => set({ selectedWMS2040_R01DetailList: id }),
	initSelectedWMS2040_R01DetailList: () => set({ selectedWMS2040_R01DetailList: undefined }),

	originalWMS2040_R01DetailList: [],
	setOriginalWMS2040_R01DetailList: (data: WMS2040_R01DetailDto[]) => set(() => ({ originalWMS2040_R01DetailList: data })),

	selectedWMS2040_R01: undefined,
	setSelectedWMS2040_R01: (itemCd: string | undefined) => set((state) => ({
		...state,
		selectedWMS2040_R01: itemCd,
	})),
	initSelectedWMS2040_R01: () => set({
		selectedWMS2040_R01: undefined,
		selectedRowInfo: undefined,
		WMS2040_R01DetailList: [],
		detailFetchTrigger: false,
	}),
	
	// 선택된 행의 복합 키 정보 (신규 추가)
	selectedRowInfo: undefined,
	setSelectedRowInfo: (rowInfo: SelectedRowInfo | undefined) => set((state) => {
		if (state.selectedRowInfo === rowInfo) return state;

		return {
			...state,
			selectedRowInfo: rowInfo,
			detailFetchTrigger: !!rowInfo,
			...(rowInfo ? {} : { WMS2040_R01DetailList: [] }),
		};
	}),
	
	// 검색 조건에서 선택된 품목 정보
	selectedItemInfo: undefined,
	setSelectedItemInfo: (itemInfo) => set({ selectedItemInfo: itemInfo }),

	// 컬럼 상태
	columnState: null,
	setColumnState: (state) => set({ columnState: state }),
	detailColumnState: null,
	setDetailColumnState: (state) => set({ detailColumnState: state }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

	allCount: 0,					setAllCount: (allCount: number) => set({ allCount }),
	detailFetchTrigger: false,
	setDetailFetchTrigger: (trigger: boolean) => set({ detailFetchTrigger: trigger }),

	// 초기화
	initData: () => set({
		WMS2040_R01Filter: defaultWMS2040_R01Filter,
		WMS2040_R01PastFilter: defaultWMS2040_R01Filter,
		WMS2040_R01List: [],
		WMS2040_R01DetailList: [],
		deletedWMS2040_R01DetailList: [],
		selectedWMS2040_R01DetailList: undefined,
		originalWMS2040_R01DetailList: [],
		selectedWMS2040_R01: undefined,
		selectedRowInfo: undefined,
		selectedItemInfo: undefined,
		columnState: null,
		detailColumnState: null,
		allCount: 0,
		scrollTop: null,
		detailScrollTop: null,
		detailFetchTrigger: false,
	}),
}));

export default useWMS2040_R01Store;